import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { formatIndianCurrency } from "../../utils/formatters";
import { AlertCircle, ArrowUpRight } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const Withdrawals = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [wallet, setWallet] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [withdrawalType, setWithdrawalType] = useState("earnings");
  const [amount, setAmount] = useState("");
  const [selectedBeneficiary, setSelectedBeneficiary] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // server-side max withdrawable for current type
  const [serverBalanceInfo, setServerBalanceInfo] = useState(null);
  // shape: { type, withdrawableBalance }

  const [checkingCanWithdraw, setCheckingCanWithdraw] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, beneficiariesRes] = await Promise.all([
        api.get("/wallets/me"),
        api.get("/bank-details"),
      ]);

      setWallet(walletRes.data);

      const bens = beneficiariesRes.data || [];
      setBeneficiaries(bens);

      // Make sure API returns bankdetailId in each item

      if (bens.length > 0) {
        const firstId = bens[0].bankdetailId; // use bankdetailId as canonical ID
        setSelectedBeneficiary(firstId);
      }

      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load wallet / beneficiaries");
      setLoading(false);
    }
  };

  const parseAmt = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  };

  // Client-side "total" balance for the chosen type (from wallets/me)
  const maxAmount = (() => {
    if (!wallet) return 0;
    if (withdrawalType === "capital") return wallet?.capitalBalance ?? 0;
    if (withdrawalType === "networking") return wallet?.networkBalance ?? 0;
    // default earnings
    return wallet?.earningsBalance ?? 0;
  })();

  // Combined "how much you can withdraw" for this type
  const withdrawableForType = (() => {
    if (
      serverBalanceInfo &&
      serverBalanceInfo.type === withdrawalType &&
      typeof serverBalanceInfo.withdrawableBalance === "number"
    ) {
      return Math.max(0, serverBalanceInfo.withdrawableBalance || 0);
    }
    // fallback to client-side total if server info not available
    return Math.max(0, maxAmount || 0);
  })();

  // --- can-withdraw check on TYPE change only ---
  useEffect(() => {
    let cancelled = false;

    const runCheck = async () => {
      try {
        setCheckingCanWithdraw(true);
        const res = await api.get("/withdrawals/can-withdraw", {
          params: {
            type: withdrawalType,
            amount: 0, // we just want max withdrawable for this type
          },
        });

        if (cancelled) return;

        const data = res?.data || {};
        console.log("can-withdraw response:", data);
        setServerBalanceInfo({
          type: withdrawalType,
          withdrawableBalance: data.withdrawableBalance ?? 0,
        });
      } catch (err) {
        if (!cancelled) {
          console.error("can-withdraw check failed:", err);
          setServerBalanceInfo(null);
        }
      } finally {
        if (!cancelled) setCheckingCanWithdraw(false);
      }
    };

    runCheck();

    return () => {
      cancelled = true;
    };
  }, [withdrawalType]); // only when type changes

  const validateBeforeSubmit = (amt) => {
    if (!user) {
      toast.error("User session expired. Please login again.");
      navigate("/login");
      return false;
    }
    // if (user?.kycStatus !== "Approved") {
    //   toast.error("KYC approval required for withdrawals");
    //   navigate("/kyc");
    //   return false;
    // }
    if (!selectedBeneficiary) {
      toast.error("Please add a beneficiary first");
      navigate("/profile");
      return false;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return false;
    }
    if (amt < 100) {
      toast.error("Minimum withdrawal amount is 100");
      return false;
    }
    // final guard: amount cannot exceed withdrawableForType
    if (amt > withdrawableForType) {
      toast.error("Amount exceeds withdrawable balance");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseAmt(amount);
    if (!validateBeforeSubmit(amt)) return;

    setSubmitting(true);
    try {
      const res = await api.post("/withdrawals", {
        type: withdrawalType,
        amount: amt,
        beneficiaryId: selectedBeneficiary, // bankdetailId is sent
      });
      const data = res?.data || {};
      console.log("Withdrawal POST response:", data);
      const { type, withdrawableBalance } = data;

      // Update server-side info based on POST response
      setServerBalanceInfo({
        type: type || withdrawalType,
        withdrawableBalance: withdrawableBalance ?? withdrawableForType,
      });

      toast.success(
        `Withdrawal request submitted. Server-validated withdrawable ${type} balance: ${formatIndianCurrency(
          withdrawableBalance ?? 0
        )}.`
      );

      await fetchData();
      setAmount("");
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.detail || "Failed to submit withdrawal request";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout showBottomNav={false}>
      <Toaster position="top-right" />
      <div className="p-4 space-y-4">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-primary font-medium text-sm mb-2"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-heading font-bold text-gray-900">
            Request Withdrawal
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Withdraw your funds to your linked bank / UPI.
          </p>
        </div>

        {/* KYC Warning */}
        {user?.kycStatus !== "Approved" && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-700 text-sm">
                  KYC Required
                </h3>
                <p className="text-xs text-gray-700 mt-1">
                  Complete KYC verification before requesting withdrawals.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Balance Card */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Available Balance</h3>
            <span className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              Live wallet snapshot
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Capital</p>
              <p className="text-lg font-mono font-bold text-primary">
                {formatIndianCurrency(wallet?.capitalBalance ?? 0)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Earnings</p>
              <p className="text-lg font-mono font-bold text-emerald-600">
                {formatIndianCurrency(wallet?.earningsBalance ?? 0)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">
                Networking / Referral
              </p>
              <p className="text-lg font-mono font-bold text-gray-900">
                {formatIndianCurrency(wallet?.networkBalance ?? 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Withdrawal Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">
              Withdrawal Details
            </h3>

            <div className="space-y-4">
              {/* Type selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Withdrawal Type *
                </label>
                <select
                  value={withdrawalType}
                  onChange={(e) => {
                    setWithdrawalType(e.target.value);
                    // amount stays; we only change type-based max
                  }}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                >
                  <option value="earnings">Earnings Balance</option>
                  <option value="capital">Capital Balance</option>
                  <option value="networking">
                    Networking / Referral Balance
                  </option>
                </select>
              </div>

              {/* Amount + balances */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  max={withdrawableForType || 0}
                  min="100"
                  step="0.01"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                />
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-gray-500">
                    Client-side available (total):{" "}
                    <span className="font-medium">
                      {formatIndianCurrency(maxAmount || 0)}
                    </span>
                  </p>

                  {checkingCanWithdraw && (
                    <p className="text-xs text-gray-500">
                      Updating server withdrawable limit…
                    </p>
                  )}

                  <p className="text-xs text-gray-800">
                    You can withdraw up to{" "}
                    <span className="font-semibold">
                      {formatIndianCurrency(withdrawableForType)}
                    </span>{" "}
                    from this balance type.
                  </p>
                </div>
              </div>

              {/* Beneficiary */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Beneficiary *
                </label>
                {beneficiaries.length > 0 ? (
                  <select
                    value={selectedBeneficiary}
                    onChange={(e) => setSelectedBeneficiary(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  >
                    {beneficiaries.map((ben, index) => (
                      <option
                        key={ben.bankdetailId || `${ben.name}-${index}`}
                        value={ben.bankdetailId}
                      >
                        {ben.name} –{" "}
                        {ben.type === "UPI"
                          ? ben.upiId
                          : `A/C ****${String(ben.accountNumber || "").slice(
                              -4
                            )}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">
                      No beneficiaries added.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/profile")}
                      className="text-primary text-sm font-semibold"
                    >
                      Add Beneficiary →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={
              submitting ||
              user?.kycStatus !== "Approved" ||
              !selectedBeneficiary ||
              !amount ||
              parseAmt(amount) < 100 ||
              parseAmt(amount) > withdrawableForType
            }
            className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-4 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            <ArrowUpRight className="w-5 h-5" />
            {submitting ? "Submitting..." : "Request Withdrawal"}
          </button>
        </form>

        {/* Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 text-sm mb-2">
            Important:
          </h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Withdrawals are subject to admin approval.</li>
            <li>• Final withdrawable balance is validated on server.</li>
            <li>• Processing time: 1-3 business days.</li>
            <li>• Ensure beneficiary details are correct.</li>
            <li>• Plan lock-in periods apply for capital withdrawals.</li>
          </ul>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Withdrawals;




